import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, RefreshCw, AlertTriangle,
  Radio, FlaskConical, ArrowUpDown, Target, Shield, Loader2
} from "lucide-react";

// ── Traffic light (reused from Strategies page) ──
function TrafficLight({ mode, status }) {
  const isGreen = status !== "ERROR" && mode === "LIVE";
  const isYellow = status !== "ERROR" && mode === "FORWARD_TEST";
  const isRed = status === "ERROR";
  return (
    <div className="flex items-center gap-1 bg-zinc-950 rounded-full px-1.5 py-0.5 border border-zinc-800">
      <span className={`w-2 h-2 rounded-full ${isRed ? "bg-rose-500 shadow-[0_0_5px_rgba(244,63,94,0.6)]" : "bg-zinc-700"}`} />
      <span className={`w-2 h-2 rounded-full ${isYellow ? "bg-amber-400 shadow-[0_0_5px_rgba(251,191,36,0.6)]" : "bg-zinc-700"}`} />
      <span className={`w-2 h-2 rounded-full ${isGreen ? "bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.6)]" : "bg-zinc-700"}`} />
    </div>
  );
}

// ── Level pill ──
function Lv({ label, value, color = "text-white" }) {
  if (value == null) return null;
  return (
    <div className="text-center">
      <span className="block text-[10px] text-zinc-500 font-mono">{label}</span>
      <span className={`block text-sm font-mono font-bold ${color}`}>
        {typeof value === "number" ? value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ── Signal Card ───────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════

function SignalCard({ card }) {
  const { signal, key_levels, risk_levels, position_info } = card;
  const hasSignal = signal?.signal_type != null;
  const isLong = signal?.direction === "LONG";
  const hasPosition = position_info != null;

  const signalLabels = {
    LE: "Long Entry", LX: "Long Exit", SE: "Short Entry",
    SX: "Short Exit", SAR: "Stop & Reverse",
  };

  return (
    <div
      data-testid={`signal-card-${card.symbol}`}
      className={`rounded-lg border overflow-hidden transition-all ${
        card.status === "ERROR"
          ? "border-rose-500/20 bg-rose-950/10"
          : hasSignal
            ? isLong ? "border-emerald-500/20 bg-zinc-900/60" : "border-rose-500/20 bg-zinc-900/60"
            : "border-zinc-800 bg-zinc-900/40"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800/50">
        <div className="flex items-center gap-3">
          <TrafficLight mode={card.mode} status={card.status} />
          <div>
            <span className="text-white font-semibold text-sm">{card.strategy_name}</span>
            <span className="text-zinc-500 text-xs font-mono ml-2">{card.symbol}</span>
            <span className="text-zinc-600 text-xs font-mono ml-1">{card.exchange}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {card.mode === "LIVE" ? (
            <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
              <Radio className="w-2.5 h-2.5" /> LIVE
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">
              <FlaskConical className="w-2.5 h-2.5" /> TEST
            </span>
          )}
          {(signal?.monthly_trend || card.monthly_trend) && (
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
              (signal?.monthly_trend || card.monthly_trend) === "BULLISH" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
              : (signal?.monthly_trend || card.monthly_trend) === "BEARISH" ? "text-rose-400 bg-rose-500/10 border-rose-500/20"
              : "text-zinc-400 bg-zinc-800 border-zinc-700"
            }`}>
              {signal?.monthly_trend || card.monthly_trend}
            </span>
          )}
        </div>
      </div>

      {/* Error state */}
      {card.status === "ERROR" && (
        <div className="px-5 py-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <span className="text-rose-300 text-sm">{card.error}</span>
        </div>
      )}

      {/* Needs seed data */}
      {card.status === "NEEDS_SEED" && (
        <div className="px-5 py-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <span className="text-amber-300 text-sm">{card.error}</span>
        </div>
      )}

      {/* Warning state */}
      {card.status === "WARN" && (
        <div className="px-5 py-3 flex items-center gap-3 border-b border-zinc-800/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-amber-300 text-xs">{card.error}</span>
        </div>
      )}

      {card.status !== "ERROR" && (
        <div className="px-5 py-4 space-y-4">
          {/* Key Levels Row */}
          <div className="flex items-center gap-6 flex-wrap">
            <Lv label="CMP" value={card.cmp} color="text-cyan-400" />
            <Lv label="Prev Top (PT)" value={key_levels?.previous_top} color="text-emerald-400" />
            <Lv label="Prev Bottom (PB)" value={key_levels?.previous_bottom} color="text-rose-400" />
            <Lv label="PM High" value={key_levels?.prev_month_high} color="text-zinc-300" />
            <Lv label="PM Low" value={key_levels?.prev_month_low} color="text-zinc-300" />
            <Lv label="Prev Close" value={key_levels?.prev_close} color="text-zinc-400" />
          </div>

          {/* Open Position Info */}
          {hasPosition && (
            <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-blue-500/5 border border-blue-500/15">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-blue-300 font-mono">
                Open {position_info.direction} @ {position_info.entry_price?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                {position_info.quantity && ` | Qty: ${position_info.quantity}`}
              </span>
            </div>
          )}

          {/* Signal & Levels */}
          {hasSignal ? (
            <div className={`rounded-md px-4 py-3 border ${
              isLong ? "bg-emerald-500/5 border-emerald-500/20" : "bg-rose-500/5 border-rose-500/20"
            }`}>
              <div className="flex items-center gap-3 mb-3">
                {isLong ? <TrendingUp className="w-5 h-5 text-emerald-400" /> : <TrendingDown className="w-5 h-5 text-rose-400" />}
                <span className={`font-bold text-sm ${isLong ? "text-emerald-400" : "text-rose-400"}`}>
                  {signalLabels[signal.signal_type] || signal.signal_type}
                </span>
                {signal.lots && (
                  <span className="text-xs font-mono text-zinc-400">
                    {signal.lots} lot{signal.lots > 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* SEP */}
              <div className="flex items-center gap-6 flex-wrap mb-3">
                <Lv label="SEP (System Entry)" value={signal.sep} color={isLong ? "text-emerald-400" : "text-rose-400"} />
                <Lv label="CMP" value={card.cmp} color="text-cyan-400" />
              </div>

              <p className="text-xs text-zinc-400 mb-3 font-mono">{signal.details}</p>

              {/* Gap handling instructions (pre-market) */}
              {signal.gap_handling && (
                <div className="rounded-md px-3 py-2.5 mb-3 bg-zinc-800/40 border border-zinc-700/30">
                  <p className="text-[10px] font-semibold text-zinc-400 mb-1.5 uppercase tracking-wider">Gap Handling (at market open)</p>
                  <div className="text-[11px] text-zinc-500 font-mono space-y-0.5">
                    <p>{signal.gap_handling.check_at}</p>
                    <p>{signal.gap_handling.gap_condition}</p>
                    <p className="text-amber-400/80">{signal.gap_handling.srep_action}</p>
                    <p className="text-zinc-400">{signal.gap_handling.normal_action}</p>
                  </div>
                </div>
              )}

              {/* Computed Entry/Exit Levels */}
              <div className="flex items-center gap-6 flex-wrap">
                <Lv label="Long Entry (SEP)" value={signal.levels?.long_entry} color="text-emerald-400" />
                <Lv label="Short Entry (SEP)" value={signal.levels?.short_entry} color="text-rose-400" />
                <Lv label="Long Exit" value={signal.levels?.long_exit} color="text-rose-300" />
                <Lv label="Short Exit" value={signal.levels?.short_exit} color="text-emerald-300" />
              </div>

              {/* Risk levels */}
              {risk_levels && (
                <div className="mt-3 pt-3 border-t border-zinc-800/30">
                  <p className="text-[10px] text-zinc-600 font-mono mb-2">
                    Risk & targets based on SEP (recalculated with actual fill price post-execution)
                  </p>
                  <div className="flex items-center gap-6 flex-wrap">
                    <Lv label="Stop Loss (RM1)" value={risk_levels.stop_loss} color="text-rose-400" />
                    <Lv label="1st Day SL (RM2)" value={risk_levels.first_day_sl} color="text-amber-400" />
                    <Lv label="Target 1 (PM1)" value={risk_levels.target1} color="text-emerald-400" />
                    <Lv label="Target 2 (PM2)" value={risk_levels.target2} color="text-emerald-300" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 rounded-md bg-zinc-800/30 border border-zinc-800">
              <ArrowUpDown className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-400">No signal — price within range</span>
              <span className="text-xs text-zinc-600 font-mono ml-auto">{signal?.details}</span>
            </div>
          )}

          {/* All computed levels even when no signal */}
          {!hasSignal && signal?.levels && (
            <div className="flex items-center gap-6 flex-wrap">
              <Lv label="Long Entry" value={signal.levels?.long_entry} color="text-emerald-400" />
              <Lv label="Short Entry" value={signal.levels?.short_entry} color="text-rose-400" />
              <Lv label="Long Exit" value={signal.levels?.long_exit} color="text-rose-300" />
              <Lv label="Short Exit" value={signal.levels?.short_exit} color="text-emerald-300" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ── Main Page ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════

export default function TradeSignals() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [monitorStatus, setMonitorStatus] = useState(null);
  const [signalTrades, setSignalTrades] = useState([]);
  const [marketStatus, setMarketStatus] = useState({});
  const [tab, setTab] = useState("signals"); // "signals" | "trades"

  const generateSignals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/strategy/generate-all-signals");
      setCards(res.data.data || []);
      if (res.data.market_status) setMarketStatus(res.data.market_status);
      setLastRefresh(new Date());
      const errors = (res.data.data || []).filter(c => c.status === "ERROR").length;
      if (errors > 0) toast.warning(`${errors} strateg${errors > 1 ? "ies" : "y"} had errors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate signals");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMonitorStatus = useCallback(async () => {
    try {
      const res = await api.get("/strategy/monitor/status");
      setMonitorStatus(res.data.data);
    } catch (e) {
      console.error("fetchMonitorStatus failed:", e);
    }
  }, []);

  const fetchSignalTrades = useCallback(async () => {
    try {
      const res = await api.get("/strategy/signal-trades");
      setSignalTrades(res.data.data || []);
    } catch (e) {
      console.error("fetchSignalTrades failed:", e);
    }
  }, []);

  useEffect(() => {
    generateSignals();
    fetchMonitorStatus();
    fetchSignalTrades();
  }, [generateSignals, fetchMonitorStatus, fetchSignalTrades]);

  // Auto-refresh monitor status every 30s when running
  useEffect(() => {
    if (!monitorStatus?.running) return;
    const interval = setInterval(() => {
      fetchMonitorStatus();
      fetchSignalTrades();
    }, 30000);
    return () => clearInterval(interval);
  }, [monitorStatus?.running, fetchMonitorStatus, fetchSignalTrades]);

  const toggleMonitor = async () => {
    try {
      if (monitorStatus?.running) {
        await api.post("/strategy/monitor/stop");
        toast.success("Market monitor stopped");
      } else {
        await api.post("/strategy/monitor/start");
        toast.success("Market monitor started");
      }
      fetchMonitorStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle monitor");
    }
  };

  const signalCards = cards.filter(c => c.signal?.signal_type != null);
  const noSignalCards = cards.filter(c => c.status === "OK" && c.signal?.signal_type == null);
  const needsSeedCards = cards.filter(c => c.status === "NEEDS_SEED");
  const warnCards = cards.filter(c => c.status === "WARN");
  const errorCards = cards.filter(c => c.status === "ERROR");

  return (
    <div className="space-y-6" data-testid="trade-signals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-white tracking-tight">
            Trade Signals
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Automated signal generation and trade execution
            {lastRefresh && (
              <span className="text-zinc-600 ml-2">
                Updated {lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Monitor Toggle */}
          <button
            data-testid="monitor-toggle-btn"
            onClick={toggleMonitor}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold transition-all border ${
              monitorStatus?.running
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20"
                : "bg-zinc-900 text-zinc-400 border-zinc-700 hover:bg-zinc-800"
            }`}
          >
            {monitorStatus?.running ? (
              <><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Monitor Running</>
            ) : (
              <><span className="w-2 h-2 rounded-full bg-zinc-600" /> Start Monitor</>
            )}
          </button>
          <button
            data-testid="refresh-signals-btn"
            onClick={() => { generateSignals(); fetchMonitorStatus(); fetchSignalTrades(); }}
            disabled={loading}
            className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-md px-5 py-2.5 text-sm transition-all shadow-[0_0_20px_-5px_rgba(6,182,212,0.4)] disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Monitor Status Bar */}
      {monitorStatus && (
        <div className="flex items-center gap-4 px-4 py-2.5 rounded-lg border border-zinc-800 bg-zinc-900/50 text-xs font-mono text-zinc-500">
          <span>IST: {monitorStatus.market_time?.substring(0, 5)}</span>
          <span className={monitorStatus.market_open ? "text-emerald-400" : "text-zinc-600"}>
            Market: {monitorStatus.market_open ? "OPEN" : "CLOSED"}
          </span>
          <span>Monitoring: {monitorStatus.strategies_monitored} strategies</span>
          {monitorStatus.states && Object.values(monitorStatus.states).map((s) => (
            <span key={s.symbol} className="text-zinc-400">
              {s.symbol}: <span className={
                s.state === "ENTERED" ? "text-emerald-400" :
                s.state === "MONITORING_ENTRY" ? "text-amber-400" :
                s.state === "WAITING_RM7" ? "text-amber-400" :
                s.state === "EXITED" ? "text-zinc-500" : "text-zinc-500"
              }>{s.state}</span>
            </span>
          ))}
        </div>
      )}

      {/* Market Status Bar */}
      {Object.keys(marketStatus).length > 0 && (
        <div className="flex items-center gap-4 bg-zinc-900/50 border border-zinc-800 rounded-md px-4 py-2">
          {Object.entries(marketStatus).map(([ex, status]) => (
            <div key={ex} className="flex items-center gap-2 text-sm">
              <span className={`w-2 h-2 rounded-full ${status.open ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
              <span className="text-zinc-400 font-medium">{ex}:</span>
              <span className={status.open ? 'text-emerald-400' : 'text-zinc-500'}>
                {status.open ? status.session || 'OPEN' : status.reason || 'CLOSED'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-zinc-900/50 rounded-md border border-zinc-800 w-fit">
        <button onClick={() => setTab("signals")} data-testid="tab-signals"
          className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
            tab === "signals" ? "bg-cyan-500/15 text-cyan-400" : "text-zinc-500 hover:text-zinc-300"
          }`}>
          Signals ({signalCards.length + noSignalCards.length})
        </button>
        <button onClick={() => setTab("trades")} data-testid="tab-trades"
          className={`px-4 py-1.5 rounded text-sm font-medium transition-all ${
            tab === "trades" ? "bg-cyan-500/15 text-cyan-400" : "text-zinc-500 hover:text-zinc-300"
          }`}>
          Recorded Trades ({signalTrades.length})
        </button>
      </div>

      {/* ══ Signals Tab ══ */}
      {tab === "signals" && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
              <span className="text-[10px] font-mono text-zinc-500 uppercase block mb-1">Active Strategies</span>
              <span className="text-2xl font-mono font-bold text-white" data-testid="stat-total">{cards.length}</span>
            </div>
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
              <span className="text-[10px] font-mono text-zinc-500 uppercase block mb-1">Signals Generated</span>
              <span className="text-2xl font-mono font-bold text-emerald-400" data-testid="stat-signals">{signalCards.length}</span>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
              <span className="text-[10px] font-mono text-zinc-500 uppercase block mb-1">In Range (No Signal)</span>
              <span className="text-2xl font-mono font-bold text-zinc-400" data-testid="stat-no-signal">{noSignalCards.length}</span>
            </div>
            <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-4">
              <span className="text-[10px] font-mono text-zinc-500 uppercase block mb-1">Needs Setup</span>
              <span className="text-2xl font-mono font-bold text-amber-400" data-testid="stat-errors">{needsSeedCards.length + warnCards.length + errorCards.length}</span>
            </div>
          </div>

          {loading && cards.length === 0 && (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">Computing signals for active strategies...</p>
            </div>
          )}

          {!loading && cards.length === 0 && (
            <div className="text-center py-16 rounded-lg border border-dashed border-zinc-800">
              <Target className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No active strategies found</p>
              <p className="text-zinc-600 text-xs mt-1">Create and activate strategies in the Strategies page first</p>
            </div>
          )}

          {signalCards.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-mono text-zinc-500 uppercase tracking-wider">Actionable Signals</h2>
              {signalCards.map((c) => <SignalCard key={c.strategy_id} card={c} />)}
            </div>
          )}

          {noSignalCards.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-mono text-zinc-500 uppercase tracking-wider">Monitoring (No Signal)</h2>
              {noSignalCards.map((c) => <SignalCard key={c.strategy_id} card={c} />)}
            </div>
          )}

          {needsSeedCards.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-mono text-amber-500/80 uppercase tracking-wider">Needs Seed Data</h2>
              {needsSeedCards.map((c) => <SignalCard key={c.strategy_id} card={c} />)}
            </div>
          )}

          {(warnCards.length > 0 || errorCards.length > 0) && (
            <div className="space-y-3">
              <h2 className="text-xs font-mono text-rose-500/80 uppercase tracking-wider">Warnings & Errors</h2>
              {warnCards.map((c) => <SignalCard key={c.strategy_id} card={c} />)}
              {errorCards.map((c) => <SignalCard key={c.strategy_id} card={c} />)}
            </div>
          )}
        </>
      )}

      {/* ══ Recorded Trades Tab ══ */}
      {tab === "trades" && (
        <div className="space-y-3">
          {signalTrades.length === 0 ? (
            <div className="text-center py-16 rounded-lg border border-dashed border-zinc-800">
              <Target className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No recorded trades yet</p>
              <p className="text-zinc-600 text-xs mt-1">Start the market monitor during trading hours to begin recording trades</p>
            </div>
          ) : (
            signalTrades.map((t) => (
              <div key={t.id} data-testid={`trade-${t.id}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {t.direction === "LONG" ? (
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-rose-400" />
                    )}
                    <span className="text-white font-semibold text-sm">{t.strategy_name}</span>
                    <span className="text-zinc-500 text-xs font-mono">{t.symbol}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                      t.mode === "LIVE" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
                        : "text-amber-400 bg-amber-500/10 border-amber-500/20"
                    }`}>{t.mode === "LIVE" ? "LIVE" : "FWD TEST"}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                      t.status === "ENTERED" ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/20"
                        : t.status === "PARTIAL_EXIT" ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
                        : t.status === "EXITED" ? "text-zinc-400 bg-zinc-800 border-zinc-700"
                        : "text-zinc-500 bg-zinc-900 border-zinc-800"
                    }`}>{t.status}</span>
                    {t.order_execution_type === "SMART_LIMIT" && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded border text-amber-300 bg-amber-500/5 border-amber-500/15">SMART</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {t.lots_remaining != null && t.lots != null && t.lots_remaining !== t.lots && (
                      <span className="text-[10px] font-mono text-amber-400">
                        {t.lots_remaining}/{t.lots} lots
                      </span>
                    )}
                    {t.total_pnl != null ? (
                      <span className={`text-sm font-mono font-bold ${t.total_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {t.total_pnl >= 0 ? "+" : ""}{t.total_pnl?.toLocaleString("en-IN", { minimumFractionDigits: 2 })} pts
                      </span>
                    ) : t.pnl != null ? (
                      <span className={`text-sm font-mono font-bold ${t.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {t.pnl >= 0 ? "+" : ""}{t.pnl?.toLocaleString("en-IN", { minimumFractionDigits: 2 })} pts/lot
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-6 flex-wrap text-center">
                  <Lv label="SEP" value={t.sep} color="text-zinc-300" />
                  {t.srep && <Lv label="SREP" value={t.srep} color="text-amber-400" />}
                  <Lv label="Entry" value={t.entry_price} color={t.direction === "LONG" ? "text-emerald-400" : "text-rose-400"} />
                  <Lv label="Entry Type" value={t.entry_type} color="text-zinc-400" />
                  {t.exit_price && <Lv label="Exit" value={t.exit_price} color="text-zinc-300" />}
                  {t.exit_reason && <Lv label="Exit Reason" value={t.exit_reason} color="text-zinc-400" />}
                  <Lv label="RM1 SL" value={t.rm1_sl} color="text-rose-400" />
                  <Lv label="RM2 SL" value={t.rm2_sl} color="text-amber-400" />
                  <Lv label="Target 1" value={t.target1} color="text-emerald-400" />
                  <Lv label="Target 2" value={t.target2} color="text-emerald-300" />
                  <Lv label="Lots" value={t.lots} color="text-zinc-300" />
                  <Lv label="MT" value={t.monthly_trend} color="text-zinc-400" />
                </div>

                {/* Partial Exits */}
                {t.partial_exits && t.partial_exits.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/50 space-y-1.5">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase">Partial Exits</span>
                    {t.partial_exits.map((pe, i) => (
                      <div key={pe.exit_time || `${pe.reason}-${i}`} className="flex items-center gap-4 text-[11px] font-mono text-zinc-400 bg-zinc-800/30 rounded px-3 py-1.5">
                        <span className="text-amber-400">{pe.reason}</span>
                        <span>{pe.lots_exited} lots @ {pe.exit_price}</span>
                        {pe.total_pnl != null && (
                          <span className={pe.total_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
                            {pe.total_pnl >= 0 ? "+" : ""}{pe.total_pnl} pts
                          </span>
                        )}
                        <span className="text-zinc-600">{pe.exit_time && new Date(pe.exit_time).toLocaleTimeString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                )}

                {t.tags && t.tags.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-3 border-t border-zinc-800/50">
                    {t.tags.map((tag) => (
                      <span key={tag} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700/50">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="text-[10px] text-zinc-600 font-mono mt-2">
                  {t.entry_time && `Entry: ${new Date(t.entry_time).toLocaleString("en-IN")}`}
                  {t.exit_time && ` | Exit: ${new Date(t.exit_time).toLocaleString("en-IN")}`}
                  {t.gap_type && ` | Gap: ${t.gap_type}`}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
